import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';

const AboutPage = () => {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    // Honeypot check
    const honeypot = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setFormState('success');
      return;
    }

    setFormState('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9pbte8senbniolessgg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await res.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (res.ok && parsed && parsed.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormState('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormState('error');
    }
  };

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 汗汗漫画',
    description: '了解汗汗漫画的品牌故事、平台特色和联系方式。汗汗漫画是领先的韩国漫画免费在线阅读平台。',
    url: 'https://hanhanmanhua.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '汗汗漫画',
      url: 'https://hanhanmanhua.com/',
    },
    about: {
      '@type': 'Organization',
      name: '汗汗漫画',
      url: 'https://hanhanmanhua.com/',
      description: '专注于韩国漫画免费在线阅读的中文平台，提供流畅的下拉式阅读体验。',
      foundingDate: '2020',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'contact@hanhanmanhua.com',
        availableLanguage: ['Chinese'],
      },
    },
  };

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '汗汗漫画是否完全免费？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，汗汗漫画平台上的所有韩国漫画内容完全免费，无需付费或订阅即可畅享阅读体验。',
        },
      },
      {
        '@type': 'Question',
        name: '如何找到我想看的韩国漫画？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过首页的分类导航浏览不同种类的韩国漫画，包括恋爱、奇幻、动作、悬疑、搞笑等类型，也可以使用搜索功能查找特定作品。',
        },
      },
      {
        '@type': 'Question',
        name: '漫画更新频率是怎样的？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '汗汗漫画平台上的热门韩漫每日更新，确保您不会错过任何精彩剧情。更新时间为每日上午10:00（北京时间）。',
        },
      },
      {
        '@type': 'Question',
        name: '支持哪些设备阅读？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '汗汗漫画支持电脑、平板和手机等多种设备，采用响应式设计，在任何设备上都能获得流畅的下拉式阅读体验。',
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 汗汗漫画 | 韩国漫画免费在线阅读平台"
        description="了解汗汗漫画的品牌故事、平台特色和联系方式。汗汗漫画是领先的韩国漫画免费在线阅读平台，提供流畅的下拉式阅读体验，每日更新热门韩漫。"
        keywords="汗汗漫画,关于我们,韩国漫画,韩漫平台,免费漫画,漫画阅读"
        canonicalUrl="https://hanhanmanhua.com/about"
        structuredData={{ ...structuredData, ...faqStructuredData }}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          {/* Breadcrumb */}
          <div className="max-w-7xl mx-auto px-4 md:px-6 pt-6">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
          </div>

          {/* Hero */}
          <section className="relative w-full h-[380px] md:h-[440px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20minimal%20abstract%20background%20with%20soft%20orange%20and%20cream%20gradients%2C%20stylized%20book%20pages%20texture%2C%20clean%20elegant%20aesthetic%2C%20modern%20library%20atmosphere%2C%20no%20text%2C%20warm%20inviting%20lighting&width=1600&height=500&seq=about-hero-hanman&orientation=landscape"
              alt="关于汗汗漫画"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
            <div className="relative z-10 flex flex-col items-center justify-center h-full px-4 md:px-6 text-center">
              <h1 className="font-heading text-3xl md:text-4xl text-background-50 mb-4">
                关于汗汗漫画
              </h1>
              <p className="text-base text-background-50/80 max-w-lg">
                致力于为中文读者提供最优质的韩国漫画阅读体验
              </p>
            </div>
          </section>

          {/* Brand Introduction */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl text-foreground-950 text-center mb-8">
                品牌介绍
              </h2>
              <div className="space-y-5 text-sm text-foreground-600 leading-relaxed">
                <p>
                  <strong className="text-foreground-950">汗汗漫画</strong>成立于2020年，是国内领先的
                  <strong className="text-foreground-950">韩国漫画</strong>（<strong className="text-foreground-950">韩漫</strong>）
                  免费在线阅读平台。我们致力于为中文读者提供最优质的韩国漫画阅读服务，
                  让每一位漫画爱好者都能轻松享受精彩的韩国漫画作品。
                </p>
                <p>
                  平台汇聚了数千部优质韩国漫画，涵盖<strong className="text-foreground-950">恋爱漫画</strong>、
                  <strong className="text-foreground-950">奇幻漫画</strong>、<strong className="text-foreground-950">动作漫画</strong>、
                  <strong className="text-foreground-950">悬疑漫画</strong>、<strong className="text-foreground-950">搞笑漫画</strong>、
                  <strong className="text-foreground-950">都市漫画</strong>、<strong className="text-foreground-950">校园漫画</strong>
                  等多种类型，满足不同读者的阅读偏好。
                </p>
                <p>
                  汗汗漫画独创的<strong className="text-foreground-950">下拉式阅读</strong>模式，
                  让用户在电脑、平板和手机上都能获得流畅舒适的阅读体验。
                  无需翻页，只需向下滚动即可无缝阅读，真正实现了"一拉到底"的畅快阅读。
                </p>
                <p>
                  我们坚持<strong className="text-foreground-950">每日更新</strong>，
                  确保读者能够第一时间追更热门韩漫的新章节。
                  平台的编辑团队每天精选优质内容，让用户不错过任何一部好看的韩国漫画。
                </p>
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl text-foreground-950 text-center mb-10">
                平台特色
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  {
                    icon: 'ri-book-open-line',
                    title: '海量韩漫资源',
                    desc: '平台收录数千部优质韩国漫画，涵盖恋爱、奇幻、动作、悬疑、搞笑、都市、校园等多种类型，丰富的漫画大全满足各种阅读口味。',
                  },
                  {
                    icon: 'ri-arrow-down-line',
                    title: '下拉式阅读',
                    desc: '创新的下拉式漫画阅读模式，流畅的竖向滚动体验，无需频繁点击翻页，沉浸在韩国漫画的精彩世界中。',
                  },
                  {
                    icon: 'ri-calendar-check-line',
                    title: '每日准时更新',
                    desc: '热门韩漫每日更新，不再错过任何精彩剧情，轻松追更。更新时间固定，帮助用户建立良好的阅读习惯。',
                  },
                  {
                    icon: 'ri-shield-star-line',
                    title: '高清画质',
                    desc: '所有漫画均采用高清画质呈现，细腻的画面表现力，还原韩国漫画原作的精美画风和细节，带来极致的视觉享受。',
                  },
                  {
                    icon: 'ri-device-line',
                    title: '多端适配',
                    desc: '支持电脑、平板、手机等多种设备，响应式设计确保在不同屏幕尺寸上都能获得舒适的漫画阅读体验。',
                  },
                  {
                    icon: 'ri-user-heart-line',
                    title: '用户至上',
                    desc: '以用户体验为核心，不断优化阅读功能和界面设计，让每一位韩国漫画爱好者都能轻松、愉快地阅读。',
                  },
                ].map((feat, i) => (
                  <div
                    key={i}
                    className="p-6 bg-background-50 border border-secondary-200/60 rounded-lg"
                  >
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                      <i className={`${feat.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-heading text-base text-foreground-950 mb-2">{feat.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{feat.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Reputation / Stats */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl text-foreground-950 text-center mb-10">
                平台口碑
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { value: '5000+', label: '韩国漫画资源' },
                  { value: '800万+', label: '月活跃读者' },
                  { value: '4.8', label: '用户平均评分' },
                  { value: '99.9%', label: '服务可用率' },
                ].map((stat, i) => (
                  <div key={i} className="text-center p-6">
                    <div className="font-heading text-3xl text-accent-500 mb-2">{stat.value}</div>
                    <p className="text-sm text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>

              {/* Testimonials */}
              <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    name: '漫画爱好者小王',
                    text: '汗汗漫画真的太棒了！下拉式阅读体验非常流畅，每天都有更新，我追的韩漫一部都没落下。强烈推荐给所有韩漫爱好者！',
                    rating: 5,
                  },
                  {
                    name: '资深漫画读者Lily',
                    text: '用了很多漫画平台，汗汗漫画的体验是最好的。界面简洁清爽，漫画种类丰富，而且完全免费，真的是良心平台。',
                    rating: 5,
                  },
                  {
                    name: '韩漫新粉小张',
                    text: '朋友推荐来的，一开始只是试试看，结果一发不可收拾。现在每天都要来汗汗漫画看更新，已经成为我生活的一部分了。',
                    rating: 5,
                  },
                ].map((testimonial, i) => (
                  <div
                    key={i}
                    className="p-6 bg-background-100 border border-secondary-200/60 rounded-lg"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: testimonial.rating }).map((_, j) => (
                        <span key={j} className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-accent-500 text-sm"></i>
                        </span>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      "{testimonial.text}"
                    </p>
                    <p className="text-xs text-foreground-500 font-medium">{testimonial.name}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl text-foreground-950 text-center mb-10">
                常见问题
              </h2>
              <div className="space-y-3">
                {[
                  {
                    q: '汗汗漫画是否完全免费？',
                    a: '是的，汗汗漫画平台上的所有韩国漫画内容完全免费，无需付费或订阅即可畅享阅读体验。我们致力于让每一位漫画爱好者都能无障碍地享受韩国漫画。',
                  },
                  {
                    q: '如何找到我想看的韩国漫画？',
                    a: '您可以通过首页的分类导航浏览不同种类的韩国漫画，包括恋爱、奇幻、动作、悬疑、搞笑、都市、校园等类型。也可以使用网站提供的漫画大全浏览所有作品。',
                  },
                  {
                    q: '漫画更新频率是怎样的？',
                    a: '汗汗漫画平台上的热门韩漫每日更新，确保您不会错过任何精彩剧情。更新时间通常为每日上午，具体以各漫画的实际更新时间为准。',
                  },
                  {
                    q: '支持哪些设备阅读？',
                    a: '汗汗漫画支持电脑、平板和手机等多种设备，采用响应式设计技术，在任何设备上都能获得流畅的下拉式阅读体验。',
                  },
                  {
                    q: '如何联系客服？',
                    a: '您可以通过下方的联系表单发送消息，或发送邮件至 contact@hanhanmanhua.com。我们的客服团队将在工作时间内尽快回复您。工作时间：工作日 9:00-18:00。',
                  },
                ].map((faq, i) => (
                  <details key={i} className="group bg-background-50 border border-secondary-200/60 rounded-lg">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-sm text-foreground-800 font-medium">
                      {faq.q}
                      <span className="w-5 h-5 flex items-center justify-center transition-transform duration-200 group-open:rotate-180">
                        <i className="ri-arrow-down-s-line"></i>
                      </span>
                    </summary>
                    <p className="px-5 pb-4 text-sm text-foreground-500 leading-relaxed">
                      {faq.a}
                    </p>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-2xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl text-foreground-950 text-center mb-3">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-8">
                有任何问题或建议？请通过以下表单与我们取得联系
              </p>

              {formState === 'success' ? (
                <div className="text-center py-12 bg-background-100 border border-secondary-200/60 rounded-lg">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-check-line text-2xl"></i>
                  </div>
                  <h3 className="font-heading text-lg text-foreground-950 mb-2">消息已发送！</h3>
                  <p className="text-sm text-foreground-500">感谢您的留言，我们会尽快回复您。</p>
                  <button
                    onClick={() => setFormState('idle')}
                    className="mt-4 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
                  >
                    发送新消息
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-5"
                >
                  {/* Honeypot */}
                  <input
                    type="text"
                    name="website_alt"
                    className="form-website-alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-secondary-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-secondary-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="contact-subject"
                      name="subject"
                      className="w-full px-4 py-2.5 bg-background-50 border border-secondary-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入消息主题"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容 <span className="text-accent-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-2.5 bg-background-50 border border-secondary-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>

                  {formState === 'error' && formError && (
                    <div className="px-4 py-3 bg-accent-100 text-accent-700 rounded-lg text-sm">
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formState === 'submitting'}
                    className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 whitespace-nowrap"
                  >
                    {formState === 'submitting' ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 border-background-50/30 border-t-background-50 rounded-full animate-spin"></span>
                        发送中...
                      </span>
                    ) : (
                      '发送消息'
                    )}
                  </button>
                </form>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default AboutPage;